/* ============================================
   Markup del partner — de nuestro costo a su precio, de una vez

   Hasta acá el partner ponía su precio ítem por ítem, y sólo en las
   cotizaciones que armaba él. Las que armamos nosotros —Nordic, Grupo
   Central— las abría, veía NUESTROS precios y no tenía dónde cargar los
   suyos: el clic en la lista llevaba al documento público, no al editor.

   Este panel es ese lugar. Tres formas de decir lo mismo, porque son las tres
   formas en que se piensa un precio: «le sumo 60%», «le sumo 5.000», «quiero
   que quede en 20.000». Las tres terminan en un multiplicador.

   El panel NO es una pantalla aparte ni un módulo nuevo: vive dentro del
   editor que el partner ya usa. Un constructor separado habría duplicado el
   editor entero —ítems, secciones, cronograma— para cambiar un número.

   La aritmética no está acá: vive en `src/lib/markup.ts`, que es también de
   donde el backend saca el factor para los precios escritos en la prosa.
   ============================================ */
import { useMemo, useState } from 'react'
import type { CotizacionItemsEstandar, Markup, MarkupTipo } from '@/lib/types'
import {
  aplicarMarkup, construirMarkup, costoBase, itemsEnOtraMoneda, itemsFijados, resumenMarkup,
} from '@/lib/markup'
import { formatCurrency, r2 } from '@/lib/utils'
import { Badge } from '@/components/ui'
import { IconAlertCircle, IconCheckCircle } from '@/components/icons/Icons'

const MODOS: { tipo: MarkupTipo; label: string; ayuda: string }[] = [
  { tipo: 'porcentaje', label: 'Porcentaje', ayuda: 'Cuánto le sumas encima a lo que nos pagas' },
  { tipo: 'monto', label: 'Monto', ayuda: 'Cuánto ganas en total con esta cotización' },
  { tipo: 'final', label: 'Precio final', ayuda: 'Cuánto quieres que pague tu cliente' },
]

interface Props {
  items: CotizacionItemsEstandar[]
  moneda: string
  markup: Markup | null
  /** Devuelve el markup y los ítems ya recalculados: el editor los adopta. */
  onAplicar: (markup: Markup, items: CotizacionItemsEstandar[]) => void
  /** Vuelve a nuestros precios: borra el markup y deja los ítems al costo. */
  onQuitar: () => void
}

export default function MarkupPartner({ items, moneda, markup, onAplicar, onQuitar }: Props) {
  const [tipo, setTipo] = useState<MarkupTipo>(markup?.tipo ?? 'porcentaje')
  const [valor, setValor] = useState<string>(markup ? String(markup.valor) : '')

  const base = useMemo(() => costoBase(items, moneda), [items, moneda])
  const fijados = useMemo(() => itemsFijados(items), [items])
  const otraMoneda = useMemo(() => itemsEnOtraMoneda(items, moneda), [items, moneda])

  /* Lo que se vería si aplicara AHORA, con lo que tiene tecleado. Se calcula
     con las mismas funciones que el guardado, así que lo que muestra es lo
     que va a quedar — no una aproximación de la pantalla. */
  const previo = useMemo(() => {
    const n = parseFloat(valor)
    if (!Number.isFinite(n)) return null
    const m = construirMarkup(tipo, n, items, moneda)
    return { markup: m, ...resumenMarkup(items, m, moneda) }
  }, [tipo, valor, items, moneda])

  // Sin ítems nuestros no hay nada que marcar: todo lo de esta cotización es
  // suyo y ya está al precio que él puso.
  if (base <= 0 && otraMoneda.length === 0 && fijados.length === 0) return null

  const aplicar = () => {
    if (!previo) return
    onAplicar(previo.markup, aplicarMarkup(items, previo.markup, moneda))
  }

  return (
    <div className="card markup-panel">
      <div className="card-header">
        <h3>Tu markup</h3>
        {markup
          ? <Badge variant="aceptada">Aplicado</Badge>
          : <Badge variant="warning">Sin aplicar — lleva nuestros precios</Badge>}
      </div>

      <div className="markup-panel__body">
        <p className="markup-panel__base">
          Esta cotización te cuesta <strong>{formatCurrency(base, moneda)}</strong>.
          {' '}Lo que le cobres a tu cliente lo decides acá.
        </p>

        <div className="markup-panel__modos">
          {MODOS.map(m => (
            <button
              key={m.tipo}
              type="button"
              className={`btn btn-sm ${tipo === m.tipo ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setTipo(m.tipo)}
            >
              {m.label}
            </button>
          ))}
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="markup-valor">
            {MODOS.find(m => m.tipo === tipo)?.ayuda}
          </label>
          <div className="markup-panel__campo">
            {tipo !== 'porcentaje' && <span className="markup-panel__unidad">{moneda}</span>}
            <input
              id="markup-valor"
              className="form-input"
              type="number"
              step="0.01"
              min="0"
              value={valor}
              onChange={e => setValor(e.target.value)}
              placeholder={tipo === 'porcentaje' ? '60' : tipo === 'monto' ? '5000' : '20000'}
            />
            {tipo === 'porcentaje' && <span className="markup-panel__unidad">%</span>}
          </div>
        </div>

        {previo && (
          <div className="markup-panel__resumen">
            <div><span>Te cuesta</span><strong>{formatCurrency(previo.costo, moneda)}</strong></div>
            <div><span>Tu cliente paga</span><strong>{formatCurrency(previo.venta, moneda)}</strong></div>
            <div className="markup-panel__resumen-margen">
              <span>Te quedan</span>
              <strong>{formatCurrency(previo.margen, moneda)} ({r2(previo.pct)}%)</strong>
            </div>
          </div>
        )}

        {/* Los dos casos en que el markup no alcanza a todo. Se dicen siempre
            que existen, no sólo al aplicar: enterarse después de guardar es
            enterarse cuando el documento ya salió. */}
        {fijados.length > 0 && (
          <p className="markup-panel__aviso">
            <IconAlertCircle />
            {fijados.length === 1
              ? '1 ítem tiene precio puesto a mano y el markup no lo toca.'
              : `${fijados.length} ítems tienen precio puesto a mano y el markup no los toca.`}
            {' '}Para que vuelvan a seguirlo, bórrales el precio y vuelve a aplicar.
          </p>
        )}
        {otraMoneda.length > 0 && (
          <p className="markup-panel__aviso">
            <IconAlertCircle />
            {otraMoneda.length === 1 ? '1 ítem nos lo pagas' : `${otraMoneda.length} ítems nos los pagas`}
            {' '}en otra moneda, así que su precio lo pones tú a mano: no hay factor que
            convierta una moneda en otra.
          </p>
        )}

        <div className="markup-panel__acciones">
          <button type="button" className="btn btn-primary" onClick={aplicar} disabled={!previo}>
            {markup ? 'Actualizar precios' : 'Aplicar a los ítems'}
          </button>
          {markup && (
            <button type="button" className="btn btn-secondary" onClick={onQuitar}>
              Volver a los precios base
            </button>
          )}
        </div>

        {markup && (
          <p className="markup-panel__nota">
            <IconCheckCircle />
            El costo base queda guardado, así que puedes cambiar este número —o dar
            un descuento— cuantas veces haga falta sin perderlo.
          </p>
        )}
      </div>
    </div>
  )
}
